import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, inr, fdate } from "@/api";
import { PageHeader, EmptyState, SlabBadge } from "@/components/Shared";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Plus, FileText, Printer, ArrowRightLeft, Pencil, Trash2 } from "lucide-react";

export default function Quotations() {
  const { user } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/quotations").then((r) => setQuotations(r.data)).catch(() => {});
    api.get("/followups/quotations").then((r) => setFollowups(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const convert = async (q) => {
    if (!window.confirm(`Convert ${q.number} to a Tax Invoice? Stock will be deducted and the client ledger posted.`)) return;
    setBusyId(q.quotation_id);
    try {
      const r = await api.post(`/quotations/${q.quotation_id}/convert`);
      toast.success(`Converted to ${r.data.number}`);
      navigate(`/invoice/${r.data.invoice_id}/print`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Conversion failed");
      load();
    } finally { setBusyId(null); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Permanently delete quotation ${q.number}?`)) return;
    try {
      await api.delete(`/quotations/${q.quotation_id}`);
      toast.success(`${q.number} deleted`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    }
  };

  return (
    <div data-testid="quotations-page">
      <PageHeader title="Proforma Invoices & Quotations" sub="Quote first — convert to a GST tax invoice with one click when the deal closes">
        <Link to="/app/quotations/new">
          <Button data-testid="new-quotation-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
            <Plus className="w-4 h-4 mr-1" /> New Quotation
          </Button>
        </Link>
      </PageHeader>

      {followups.length > 0 && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 mb-6" data-testid="quotation-followups-panel">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            <h3 className="font-semibold text-red-700">
              Follow-up Needed — {followups.length} open quote{followups.length > 1 ? "s" : ""} past validity
            </h3>
          </div>
          <div className="space-y-2">
            {followups.map((q) => (
              <div key={q.quotation_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white border border-slate-200 px-4 py-2.5" data-testid={`followup-${q.quotation_id}`}>
                <div>
                  <span className="font-mono2 font-semibold text-slate-900 mr-2">{q.number}</span>
                  <span className="text-sm text-slate-700">{q.client.name}</span>
                  {q.client.phone && <span className="text-xs text-slate-500 ml-2">{q.client.phone}</span>}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-red-600 font-mono2">{q.days_overdue}d overdue</span>
                  <span className="font-mono2 font-bold text-emerald-600">{inr(q.grand_total)}</span>
                  <Link to={`/quotation/${q.quotation_id}/print`} className="text-xs text-emerald-600 hover:text-emerald-700 transition-colors duration-150" data-testid={`followup-view-${q.quotation_id}`}>View quote</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {quotations.length === 0 ? (
        <EmptyState icon={FileText} title="No quotations yet" sub="Create a proforma invoice for a client" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]" data-testid="quotations-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Quote #</th><th className="px-5 py-3">Client</th>
                <th className="px-5 py-3">Slab</th><th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Valid Until</th><th className="px-5 py-3 text-right">Total</th>
                <th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {quotations.map((q) => (
                <tr key={q.quotation_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`quotation-row-${q.quotation_id}`}>
                  <td className="px-5 py-3.5 font-mono2 font-semibold text-slate-900">{q.number}</td>
                  <td className="px-5 py-3.5 text-slate-800">{q.client.name}</td>
                  <td className="px-5 py-3.5"><SlabBadge slab={q.slab} /></td>
                  <td className="px-5 py-3.5 text-slate-600">{fdate(q.date)}</td>
                  <td className="px-5 py-3.5 text-slate-600">{fdate(q.valid_until)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 font-bold text-emerald-600">{inr(q.grand_total)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full border text-[11px] font-mono2 uppercase ${q.status === "converted" ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/10" : "border-amber-500/40 text-amber-600 bg-amber-500/10"}`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right space-x-1">
                    <Link to={`/quotation/${q.quotation_id}/print`} data-testid={`quotation-print-${q.quotation_id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                      <Printer className="w-3.5 h-3.5" /> View
                    </Link>
                    {q.status === "open" && (
                      <button onClick={() => convert(q)} disabled={busyId === q.quotation_id} data-testid={`quotation-convert-${q.quotation_id}`}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-500/10 transition-colors duration-150 disabled:opacity-50">
                        <ArrowRightLeft className="w-3.5 h-3.5" /> {busyId === q.quotation_id ? "Converting…" : "Convert to Invoice"}
                      </button>
                    )}
                    {user?.role === "admin" && (
                      <>
                        <Link to={`/app/quotations/${q.quotation_id}/edit`} data-testid={`quotation-edit-${q.quotation_id}`}
                          className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors duration-150">
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </Link>
                        <button onClick={() => remove(q)} data-testid={`quotation-delete-${q.quotation_id}`}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
